/**
 * WHAT A CHAT MESSAGE IS — once, for every chat this product ever grows.
 *
 * `COMMUNICATION.md §8.1`: there will be **many chats**, and where each belongs is deliberately not
 * decided yet. What IS decided is that they are all the same infrastructure and the same schema —
 * and that binds from the FIRST chat rather than the second, because a second one arriving with its
 * own message shape and its own idea of what a message may contain is not a second feature. It is a
 * fork of the first, and every rule about who may type, what is bounded and what is dropped would
 * then have to be argued twice and would differ in one of them.
 *
 * This repository has that scar twice already: *"a rule copied into two clients is a rule that will
 * differ in one of them"* is written beside `mustBeAsked`, and `licence-policy.ts` exists because
 * two files stated one policy differently and disagreed.
 *
 * All of this lived in `web/src/social/livekitConnector.ts` until it was moved here — the shape, the
 * length, and the rules for reading text nobody wrote for us. One surface, one file, and nothing a
 * second surface could reuse without copying it.
 *
 * WHAT IS DELIBERATELY NOT HERE: the transport. In-room chat rides the LiveKit data channel (D12)
 * because the join token already grants it; another chat might be answered by the server over HTTP.
 * Those are different wires carrying the same message, and a wire format in here would be a
 * decision about where chats live — which §8.1 leaves open on purpose.
 */

/**
 * The longest message any chat here will carry. Enough for a question, short of a pasted essay.
 *
 * One number rather than one per surface: a reader who learns what fits in one chat should not be
 * surprised by another, and a cap that differs between two chats is a cap nobody can describe.
 */
export const MAX_CHAT_MESSAGE = 500;

/**
 * Something somebody typed, as any chat surface renders it.
 *
 * `identity` is the sender's user id — the same id a room's roster and its host controls use (D10),
 * so a surface can tell a reader's own words from somebody else's without a second lookup, and can
 * hush a sender by the id it already knows.
 *
 * Nothing here says how long it lives. In-room chat is ephemeral by design and goes when the room
 * does (D23); whether some later chat is durable is a product question §8.1 leaves open, and a
 * field for it now would be a field nobody could remove.
 */
export interface ChatMessage {
  /** Unique within the surface that produced it, so a list can key on it with no round trip. */
  id: string;
  /** The sender's user id — the same id the roster and the host controls use. */
  identity: string;
  name: string;
  text: string;
  at: number;
}

/**
 * AN ID FOR A MESSAGE, from a number the RECEIVER owns — which is the whole point of it.
 *
 * Concatenating two things hardly needs a function. What needs stating is where the second thing
 * may come from, because the in-room chat got it wrong: it built ids from the sender's identity,
 * the timestamp THE SENDER PUT IN THE PACKET, and a counter that only moved when this browser sent
 * something of its own. Everything received between two of our own sends therefore shared that
 * counter, and the only thing left separating one message from another was a number the sender
 * chose.
 *
 * A sender chooses it. Twenty messages all claiming the same millisecond arrive as twenty identical
 * ids, and a list keying on them reads that as one message changing rather than twenty appearing:
 * the nodes are mis-associated and the conversation everybody else is reading comes apart. That is
 * the same harm the drop-rather-than-throw rule above exists to prevent, arriving by another door —
 * one bad sender must not break the chat for the room.
 *
 * So the sequence belongs to whoever is reading, and this signature is the reminder: there is
 * nowhere in it to put something off the wire.
 */
/**
 * THE LARGEST PACKET A CHAT WILL EVEN LOOK AT, in bytes.
 *
 * `chatTextOf` bounds what a message may SAY, and it does that after the bytes have been decoded
 * and parsed — so a sender who puts fifty megabytes on the wire has already been decoded and
 * parsed by every reader in the room before anything says no. `JSON.parse` on the main thread is
 * not interruptible: the tab stops, the audio element with it. **One bad sender must not break the
 * chat for everyone else** is the rule `chatTextOf` exists for, and this is the same harm arriving
 * one step earlier, before any of that code has run.
 *
 * DERIVED, not chosen. UTF-8 spends at most four bytes on one UTF-16 code unit, so a message of
 * `MAX_CHAT_MESSAGE` units cannot exceed that many times four — a loose upper bound on purpose,
 * since the point is to refuse the absurd rather than to trim the ordinary. The rest is room for
 * the envelope a surface wraps it in. A number typed here instead would be the third copy of a cap
 * this file exists to state once.
 */
export const MAX_CHAT_PACKET = MAX_CHAT_MESSAGE * 4 + 256;

/**
 * HOW MANY A CHAT HOLDS ON SCREEN.
 *
 * A room is not a scrollback. The durable record of what was said about a passage is the
 * discussion, which is a different product with a different lifetime (D23) — and an unbounded list
 * in a room somebody leaves open overnight is a leak.
 */
export const CHAT_KEPT = 200;

/**
 * HOW CLOSE TO THE END COUNTS AS FOLLOWING THE CONVERSATION, in whatever a surface measures in.
 *
 * Not zero, because a list is never exactly at its end: fractional device pixels, a partly drawn
 * last row and the browser's own rounding all leave a reader who IS at the bottom a pixel or two
 * short of it. A rule written as `=== 0` would decide that everybody had scrolled up and would
 * follow nobody, which is the same defect in the mirror.
 */
export const CHAT_FOLLOW_WITHIN = 48;

/**
 * SHOULD THE LIST JUMP TO THE NEWEST MESSAGE — asked of where the reader was, not of the message.
 *
 * The web panel scrolled to the end on every arrival, unconditionally. A reader scrolled up through
 * what was said — the thing a 200-message window exists to let them do — was thrown back to the
 * bottom by every message anybody typed, and during the raid the hush exists for, by every message
 * a raider typed. So the flooder took the conversation away from a reader who had already hushed
 * them, by a route no moderation control touches.
 *
 * A chat that never followed would be worse for everyone else: the normal case is a reader at the
 * end waiting for the next line, and making them chase it is not a fix. So: follow the reader who
 * is following, leave alone the reader who is reading.
 *
 * `distanceFromBottom` is whatever the surface can measure — `scrollHeight - scrollTop -
 * clientHeight` in a browser, the equivalent from a native list's own offsets. The unit is the
 * surface's; the decision is not.
 */
export function chatFollows(distanceFromBottom: number): boolean {
  return distanceFromBottom <= CHAT_FOLLOW_WITHIN;
}

/**
 * A MESSAGE ARRIVES — and everything a chat decides at that moment, in one function.
 *
 * Three rules, and all three were the panel's private business until a second chat was in sight:
 * how many are held, that somebody hushed is dropped **on arrival** rather than hidden at render,
 * and that the list only changes when something actually changed.
 *
 * The middle one is the deliberate part. Hiding at render would let a flooder a reader has hushed
 * push the conversation they DO want out of the window while being invisible — the raid the hush
 * exists for, winning anyway. The cost is that un-hushing does not bring back what was said while
 * they were hushed, which is the honest account: the reader was not listening.
 *
 * Returns the list it was given when the message is dropped, so a surface holding this in state
 * does not re-render for a message nobody will see.
 */
export function chatArrived(
  kept: readonly ChatMessage[],
  message: ChatMessage,
  hushed: readonly string[],
): readonly ChatMessage[] {
  if (hushed.includes(message.identity)) return kept;
  return [...kept, message].slice(-CHAT_KEPT);
}

/**
 * SOMEBODY WAS JUST HUSHED — and what they already said goes with them.
 *
 * The half of that control which did nothing: dropping on arrival left the screen exactly as it
 * was at the moment of the press, so a reader being shouted at hushed the shouter and went on
 * reading it. This control is for a raid, and during a raid the words already on screen are the
 * harm.
 *
 * The SAME list back when nothing went, and that is load-bearing rather than tidy: a fresh array is
 * never `Object.is`-equal to the old one, so a React surface whose parent rebuilds `hushed` each
 * render would set state, re-render, and set it again forever.
 */
export function chatHushed(
  kept: readonly ChatMessage[],
  hushed: readonly string[],
): readonly ChatMessage[] {
  const left = kept.filter(message => !hushed.includes(message.identity));
  return left.length === kept.length ? kept : left;
}

/**
 * THE TWO THINGS A CHAT SURFACE NEEDS — the seam every later chat arrives through.
 *
 * Not a contradiction of the note above about transport. A wire format would say how a message
 * crosses the gap; this says only that something on the other side can be typed into and listened
 * to. Which wire it is stays open, which is the point: the in-room chat satisfies this with the
 * LiveKit data channel the join token already grants (D12), and a Hall chat answered by our own
 * server over HTTP would satisfy the same two verbs with no chat surface changing at all.
 *
 * It exists because §8.1 binds the SCHEMA from the first chat and that is only half of it. The
 * rules a chat panel keeps — how a list keys, how many are held, whose words are dropped on
 * arrival, what happens to a message that did not go out — are as easy to fork as the shape, and
 * a panel that demanded a whole `VoiceSession` forced the fork: a chat with no voice in it could
 * not have reused a line of it, so the second chat would have been written again, and one of the
 * two would have kept fewer messages or lost a reader's words on a failed send.
 *
 * Both verbs are here rather than one because a surface that can only listen is a transcript.
 */
export interface ChatChannel {
  /** Type into it. What is sent is bounded by `chatTextOf`, whichever wire carries it. */
  sendMessage(text: string): Promise<void>;
  /** Subscribe to what others type; returns its own unsubscribe, so an unmount can stop listening. */
  onMessage(listener: (message: ChatMessage) => void): () => void;
}

export function chatIdOf(identity: string, seq: number): string {
  return `${identity}:${seq}`;
}

/**
 * THE TEXT OF A MESSAGE, OR NULL — the one place that decides what a chat will carry.
 *
 * Everything a chat receives was typed by somebody else, and in the in-room case it arrives from
 * another participant's BROWSER rather than from our server: nothing about it is trusted, not that
 * it is a string, not that it holds anything but spaces, and not that it is a sane length.
 *
 * Returns null rather than throwing, and that is the rule this exists to keep in one place: one bad
 * sender must not break the chat for everyone else in the room. A surface that threw here would
 * hand a raider a way to silence a conversation by typing.
 *
 * What it does NOT do is escape anything. A message is rendered as text and never as markup, which
 * is a fact about the renderer — HTML, a native `<Text>`, a terminal — and cannot be settled by a
 * string function. Every surface owes that separately.
 */
export function chatTextOf(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, MAX_CHAT_MESSAGE);
}
